import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Users, Wrench, CheckCircle, XCircle, Archive, RotateCcw, Edit, Key } from 'lucide-react';
import { toast } from 'sonner';
import { useActivity } from '../hooks/useActivity';
import { useUpdateActivityStatus } from '../hooks/useUpdateActivityStatus';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import type { ActivityStep, ActivityTip, ActivityWhyItWorks, ActivityStatus } from '../types/activity';

/**
 * Helper to check if error is an Error object
 */
function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Activity Details page - view and manage single activity
 */
export default function ActivityDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch activity data
  const { data, isLoading, isError: hasError, error } = useActivity(id!);

  // Update document title
  useEffect(() => {
    if (data) {
      document.title = `${data.data.title} | Admin Dashboard`;
    }
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, [data]);

  // Show error toast if query fails
  useEffect(() => {
    if (hasError && error) {
      const errorMessage = isError(error) ? error.message : 'Failed to load activity';
      toast.error(errorMessage);
    }
  }, [hasError, error]);

  // Handle navigation back to activities list
  const handleBack = () => {
    navigate('/admin/activities');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb skeleton */}
        <div className="animate-pulse">
          <div className="h-4 bg-admin-dark-700 rounded w-48"></div>
        </div>

        {/* Page header skeleton */}
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-admin-dark-700 rounded w-2/3"></div>
          <div className="h-4 bg-admin-dark-700 rounded w-32"></div>
        </div>

        {/* Content skeleton */}
        <div className="admin-card p-6 space-y-4 animate-pulse">
          <div className="h-4 bg-admin-dark-700 rounded w-full"></div>
          <div className="h-4 bg-admin-dark-700 rounded w-5/6"></div>
          <div className="h-4 bg-admin-dark-700 rounded w-4/6"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError || !data) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-admin-slate-400 hover:text-admin-slate-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-body">Back to Activities</span>
        </button>

        <div className="admin-card p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-admin-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-admin-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-heading-4 text-admin-slate-400 mb-2">
              Activity Not Found
            </h3>
            <p className="text-body-sm text-admin-slate-500 mb-6">
              The activity you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
            <button onClick={handleBack} className="admin-button-primary">
              Return to Activities
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activity = data.data;

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-body-sm">
        <button
          onClick={handleBack}
          className="text-admin-slate-500 hover:text-admin-slate-400 transition-colors"
        >
          Activities
        </button>
        <span className="text-admin-slate-600">/</span>
        <span className="text-admin-slate-400">{activity.title}</span>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex-1">
            <h1 className="text-heading-1 text-admin-slate-400">{activity.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/admin/activities/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-admin-accent-blue hover:bg-admin-accent-blue/90 text-white rounded-lg font-medium transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit Activity
            </button>
            <StatusBadge status={activity.status} />
          </div>
        </div>
        <p className="text-body text-admin-slate-500">
          Last updated {new Date(activity.updatedAt).toLocaleDateString()}
        </p>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="admin-card p-6"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">Description</h2>
            <p className="text-body text-admin-slate-500">{activity.description}</p>
          </motion.div>

          {/* Image */}
          {activity?.imageUrl && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="admin-card p-6"
            >
              <h2 className="text-heading-3 text-admin-slate-400 mb-4">Activity Image</h2>
              <img
                src={activity.imageUrl}
                alt={activity.title}
                className="w-full rounded-lg"
              />
            </motion.div>
          )}

          {/* Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="admin-card p-6"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">Steps</h2>
            <StepsList steps={activity.steps} />
          </motion.div>

          {/* Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="admin-card p-6"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">
              Tips to Make Play Last Longer
            </h2>
            <TipsList tips={activity.tips} />
          </motion.div>

          {/* Why It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="admin-card p-6"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">Why It Works</h2>
            <WhyItWorksContent content={activity.whyItWorks} />
          </motion.div>
        </div>

        {/* Right Column - Metadata & Actions */}
        <div className="space-y-6">
          {/* Activity Metadata */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="admin-card p-6 space-y-4"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">Details</h2>

            {/* Duration */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-admin-dark-700 flex items-center justify-center">
                <Clock className="w-5 h-5 text-admin-accent-blue" />
              </div>
              <div>
                <p className="text-body-sm text-admin-slate-600">Duration</p>
                <p className="text-body text-admin-slate-400">{activity.duration} minutes</p>
              </div>
            </div>

            {/* Play Type */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-admin-dark-700 flex items-center justify-center">
                <Users className="w-5 h-5 text-admin-accent-purple" />
              </div>
              <div>
                <p className="text-body-sm text-admin-slate-600">Play Type</p>
                <p className="text-body text-admin-slate-400">{activity.playTypeText}</p>
              </div>
            </div>

            {/* Prep Needed */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-admin-dark-700 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-body-sm text-admin-slate-600">Preparation</p>
                <p className="text-body text-admin-slate-400">{activity.prepNeededText}</p>
              </div>
            </div>

            {/* Activity Key */}
            {activity.activityKey && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-admin-dark-700 flex items-center justify-center">
                  <Key className="w-5 h-5 text-admin-slate-500" />
                </div>
                <div>
                  <p className="text-body-sm text-admin-slate-600">Activity Key</p>
                  <p className="text-body text-admin-slate-400 font-mono">{activity.activityKey}</p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Category */}
          {activity.category && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="admin-card p-6"
            >
              <h2 className="text-heading-3 text-admin-slate-400 mb-4">Category</h2>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-body-sm font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20">
                {activity.category.color && (
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: activity.category.color }}
                  />
                )}
                {activity.category.name}
              </span>

              {/* Pack Name — shown below category when available */}
              {activity.packName && (
                <div className="mt-4">
                  <p className="text-body-sm text-admin-slate-600 mb-2">Pack</p>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-body-sm font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20">
                    {activity.packName.name}
                  </span>
                </div>
              )}
            </motion.div>
          )}

          {/* Tags */}
          {activity.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="admin-card p-6"
            >
              <h2 className="text-heading-3 text-admin-slate-400 mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {activity.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-body-sm font-medium bg-admin-accent-purple/10 text-admin-accent-purple border border-admin-accent-purple/20"
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Time Periods */}
          {activity.transitions && activity.transitions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.17 }}
              className="admin-card p-6"
            >
              <h2 className="text-heading-3 text-admin-slate-400 mb-4">Time Periods</h2>
              <div className="flex flex-wrap gap-2">
                {activity.transitions.map((transition) => (
                  <span
                    key={transition.id}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-body-sm font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20"
                  >
                    {transition.label}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Age Groups */}
          {activity.ageRanges && activity.ageRanges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.19 }}
              className="admin-card p-6"
            >
              <h2 className="text-heading-3 text-admin-slate-400 mb-4">Age Groups</h2>
              <div className="flex flex-wrap gap-2">
                {activity.ageRanges.map((ar) => (
                  <span
                    key={`${ar.fromAge}-${ar.toAge}`}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-body-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20"
                  >
                    {ar.fromAge}–{ar.toAge} yrs
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Status Management - Placeholder for Phase 4 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="admin-card p-6"
          >
            <h2 className="text-heading-3 text-admin-slate-400 mb-4">Status Management</h2>
            <StatusManagementControls
              activityId={activity.id}
              currentStatus={activity.status}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/**
 * Status management controls component
 */
function StatusManagementControls({
  activityId,
  currentStatus,
}: {
  activityId: string;
  currentStatus: string;
}) {
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const updateStatus = useUpdateActivityStatus();

  // Handle status transitions
  const handlePublish = () => {
    updateStatus.mutate({ 
      id: activityId, 
      status: 'published',
      previousStatus: currentStatus as ActivityStatus,
    });
  };

  const handleUnpublish = () => {
    updateStatus.mutate({ 
      id: activityId, 
      status: 'draft',
      previousStatus: currentStatus as ActivityStatus,
    });
  };

  const handleArchive = () => {
    updateStatus.mutate({ 
      id: activityId, 
      status: 'archived',
      previousStatus: currentStatus as ActivityStatus,
    });
    setShowArchiveDialog(false);
  };

  const handleRestoreToDraft = () => {
    updateStatus.mutate({ 
      id: activityId, 
      status: 'draft',
      previousStatus: currentStatus as ActivityStatus,
    });
  };

  const isUpdating = updateStatus.isPending;

  // Draft status: Show Publish and Archive buttons
  if (currentStatus === 'draft') {
    return (
      <>
        <div className="space-y-3">
          <button
            onClick={handlePublish}
            disabled={isUpdating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-4 h-4" />
            {isUpdating ? 'Publishing...' : 'Publish Activity'}
          </button>

          <button
            onClick={() => setShowArchiveDialog(true)}
            disabled={isUpdating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-admin-dark-700 hover:bg-admin-dark-600 text-amber-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-amber-500/30"
          >
            <Archive className="w-4 h-4" />
            Archive Activity
          </button>
        </div>

        <ArchiveConfirmationDialog
          open={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={handleArchive}
          isLoading={isUpdating}
        />
      </>
    );
  }

  // Published status: Show Unpublish and Archive buttons
  if (currentStatus === 'published') {
    return (
      <>
        <div className="space-y-3">
          <button
            onClick={handleUnpublish}
            disabled={isUpdating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-admin-slate-300/20"
          >
            <XCircle className="w-4 h-4" />
            {isUpdating ? 'Unpublishing...' : 'Unpublish Activity'}
          </button>

          <button
            onClick={() => setShowArchiveDialog(true)}
            disabled={isUpdating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-admin-dark-700 hover:bg-admin-dark-600 text-amber-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-amber-500/30"
          >
            <Archive className="w-4 h-4" />
            Archive Activity
          </button>
        </div>

        <ArchiveConfirmationDialog
          open={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={handleArchive}
          isLoading={isUpdating}
        />
      </>
    );
  }

  // Archived status: Show Restore to Draft button
  if (currentStatus === 'archived') {
    return (
      <div>
        <button
          onClick={handleRestoreToDraft}
          disabled={isUpdating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-admin-slate-300/20"
        >
          <RotateCcw className="w-4 h-4" />
          {isUpdating ? 'Restoring...' : 'Restore to Draft'}
        </button>
        <p className="text-body-sm text-admin-slate-600 mt-3">
          This activity is archived. Restore it to draft to make changes or publish it again.
        </p>
      </div>
    );
  }

  return null;
}

/**
 * Archive confirmation dialog
 */
function ArchiveConfirmationDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-admin-dark-800 border border-admin-slate-300/10">
        <DialogHeader>
          <DialogTitle className="text-heading-3 text-admin-slate-400">
            Archive Activity?
          </DialogTitle>
          <DialogDescription className="text-body text-admin-slate-500">
            Archiving this activity will remove it from the active list. You can restore
            it to draft later if needed.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-3 sm:gap-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Archiving...' : 'Archive Activity'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: string }) {
  const colors = {
    draft: 'bg-admin-slate-600/20 text-admin-slate-400 border-admin-slate-600/30',
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };

  const color = colors[status as keyof typeof colors] || colors.draft;

  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-lg text-body-sm font-medium border ${color}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/**
 * Steps list component
 */
function StepsList({ steps }: { steps: ActivityStep[] }) {
  return (
    <ol className="space-y-3">
      {steps.map((step, index) => (
        <li key={index} className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-admin-accent-blue/20 text-admin-accent-blue flex items-center justify-center text-body-sm font-medium">
            {index + 1}
          </span>
          <div className="flex-1">
            <p className="text-body text-admin-slate-400">
              {step.emphasis ? (
                <>
                  <span className="font-semibold text-admin-accent-blue">
                    {step.emphasis}
                  </span>{' '}
                  {step.text.replace(step.emphasis, '')}
                </>
              ) : (
                step.text
              )}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Tips list component
 */
function TipsList({ tips }: { tips: ActivityTip[] }) {
  const renderTextWithHighlight = (tip: ActivityTip) => {
    if (!tip.boldText || !tip.text.includes(tip.boldText)) {
      // No boldText or not found in text, render normally
      return <span className="text-admin-slate-400">{tip.text}</span>;
    }

    // Split text into parts: before, bold, after
    const index = tip.text.indexOf(tip.boldText);
    const before = tip.text.substring(0, index);
    const bold = tip.boldText;
    const after = tip.text.substring(index + tip.boldText.length);

    return (
      <>
        {before && <span className="text-admin-slate-400">{before}</span>}
        <span className="font-semibold text-admin-accent-purple">{bold}</span>
        {after && <span className="text-admin-slate-400">{after}</span>}
      </>
    );
  };

  return (
    <ul className="space-y-3">
      {tips.map((tip, index) => (
        <li key={index} className="flex gap-3">
          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-admin-accent-purple mt-2"></span>
          <p className="flex-1 text-body">
            {renderTextWithHighlight(tip)}
          </p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Why It Works content component
 */
function WhyItWorksContent({ content }: { content: ActivityWhyItWorks }) {
  // Split text by bold keywords to highlight them
  const renderTextWithHighlights = () => {
    if (!content.boldKeywords || content.boldKeywords.length === 0) {
      return <p className="text-body text-admin-slate-400">{content.text}</p>;
    }

    // Create regex pattern to match bold keywords
    const pattern = new RegExp(`(${content.boldKeywords.join('|')})`, 'gi');
    const parts = content.text.split(pattern);

    return (
      <p className="text-body text-admin-slate-400">
        {parts.map((part, index) => {
          const isBold = content.boldKeywords.some(
            (keyword) => keyword.toLowerCase() === part.toLowerCase()
          );
          return isBold ? (
            <span key={index} className="font-semibold text-green-400">
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </p>
    );
  };

  return (
    <div className="bg-admin-dark-700/50 rounded-lg p-4 border border-green-500/20">
      {renderTextWithHighlights()}
    </div>
  );
}
